import React from 'react'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'

import { calculatePartDayHours, computeDaysAndHours, getFormattedDaysAndHours, getRawDaysOrHours } from '../../functions/get-days-or-hours'

import { IDisplayLeaveDuration, IDisplayLeaveDurationBots, IDisplayLeaveInDaysAndHoursBots, LeaveDisplayFormatEnum } from '../../types/display-in-days-hours'
import { IToilRequest } from '../../types/toil-request'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '../../data/app-parameters'
import { getAverageWorkingHours, userHasWorkingHoursSchedule } from '../../functions/work-week'

export const DisplayLeaveDuration = ({ value, format = LeaveDisplayFormatEnum.long, daysList, hasSchedule }: IDisplayLeaveDuration): React.ReactElement => {
  const { formatMessage } = useIntl()
  if (value === 0) {
    return <>{value}</>
  }

  const totalLeaveHours = daysList?.reduce((acc, day) => acc + day.leaveHours, 0) || 0

  if (hasSchedule) {
    // We only care about raw hours, since its a schedule - put zeros for everything else
    const quota = { raw: { hours: totalLeaveHours, days: 0 }, days: 0, hours: totalLeaveHours, minutes: 0, isNegative: false }
    return <>{getRawDaysOrHours(quota, 'hours', formatMessage, LeaveDisplayFormatEnum.short)}</>
  }

  //new leaves will have daysList, old ones(8h) will not
  const numberOfWorkingHoursPerDay = daysList?.[0]?.workingHoursInDay || DEFAULT_WORKING_HOURS_IN_DAY

  // `value` is always in DAYS (workingDays semantics) across every caller. We convert to
  // hours here - `computeDaysAndHours` expects an hours input. `daysList` provides the more
  // accurate hours-per-day total (used when present); otherwise fall back to
  // `value * numberOfWorkingHoursPerDay`. The `hourlyLeaveAccounting` flag no longer alters
  // this conversion - previously it caused hourly companies to interpret days as hours,
  // rendering 4h leaves as "30 minutes" and 1-day leaves as "1 hour".
  const totalLeaveHoursFromWorkingDays = value * numberOfWorkingHoursPerDay
  const val = totalLeaveHours > 0 ? totalLeaveHours : totalLeaveHoursFromWorkingDays
  // We always want to display minutes in the leave duration (`true` argument in `computeDaysAndHours` function)
  // because the minutes are displayed only when they are not 0.
  // The minutes can be increments of 15 in case customers enable shortest leave intervals than 1 hours (15 or 30 minutes).
  const leave = computeDaysAndHours(val, numberOfWorkingHoursPerDay, true)

  return (
    <>
      {getFormattedDaysAndHours({
        leave,
        formatMessage,
        displayFormat: format,
      })}
    </>
  )
}

export const displayLeaveDuration = ({ hourlyLeaveAccounting = false, value, format = LeaveDisplayFormatEnum.long, daysList, formatMessage }: IDisplayLeaveDurationBots) => {
  if (value === 0) {
    return `${value}`
  }
  //new leaves will have daysList, old ones(8h) will not
  const numberOfWorkingHoursPerDay = daysList?.[0]?.workingHoursInDay || DEFAULT_WORKING_HOURS_IN_DAY

  const val = hourlyLeaveAccounting ? value : value * numberOfWorkingHoursPerDay
  // We always want to display minutes in the leave duration (`true` argument in `computeDaysAndHours` function)
  // because the minutes are displayed only when they are not 0.
  // The minutes can be increments of 15 in case customers enable shortest leave intervals than 1 hours (15 or 30 minutes).
  const leave = computeDaysAndHours(val, numberOfWorkingHoursPerDay, true)
  return getFormattedDaysAndHours({
    leave,
    formatMessage,
    displayFormat: format,
  })
}

export const displayLeaveInDaysAndHours = ({
  hourlyLeaveAccounting = false,
  value,
  format = LeaveDisplayFormatEnum.long,
  formatMessage,
  workingHours,
}: IDisplayLeaveInDaysAndHoursBots) => {
  if (value === 0) {
    return `${value}`
  }

  const hasSchedule = userHasWorkingHoursSchedule(workingHours)
  if (hasSchedule) {
    // We only care about raw hours, since its a schedule - everything else can be zeros
    const quota = {
      raw: {
        hours: value,
        days: 0,
      },
      days: 0,
      hours: value,
      minutes: 0,
      isNegative: false,
    }
    // We know that user quota is set in hours when they have a working hours schedule
    return getRawDaysOrHours(quota, 'hours', formatMessage, LeaveDisplayFormatEnum.short)
  }

  // When there's no schedule, we get an average working hours per day (which is the same for every day in this case anyway)
  const numberOfWorkingHoursPerDay = getAverageWorkingHours(workingHours)

  const val = hourlyLeaveAccounting ? value : value * numberOfWorkingHoursPerDay
  const leave = computeDaysAndHours(val, numberOfWorkingHoursPerDay, true)
  return getFormattedDaysAndHours({
    leave,
    formatMessage,
    displayFormat: format,
  })
}

type ToilRequestDisplay = Pick<IToilRequest, 'isPartDay' | 'partDay' | 'partDayEndHour' | 'partDayStartHour' | 'daysList'> & { startDate?: string; endDate?: string }
export const displayToilInDaysAndHours = (formatMessage, toilRequest: ToilRequestDisplay) => {
  let amount: number | undefined
  if (toilRequest.isPartDay) {
    if (toilRequest.partDay) {
      amount = calculatePartDayHours(toilRequest.partDay)
    } else if (typeof toilRequest.partDayEndHour === 'number' && typeof toilRequest.partDayStartHour === 'number') {
      amount = toilRequest.partDayEndHour - toilRequest.partDayStartHour
    }
  } else {
    amount = toilRequest.daysList?.length
  }

  // Fallback for full-day records whose daysList was dropped: inclusive day span from
  // start/end dates. Skipped for part-day leaves - a day count rendered as "N hours" is
  // actively wrong when partDay bounds are missing.
  if (!amount || Number.isNaN(amount)) {
    if (!toilRequest.isPartDay && toilRequest.startDate && toilRequest.endDate) {
      amount = Math.max(1, dayjs(toilRequest.endDate).diff(dayjs(toilRequest.startDate), 'day') + 1)
    } else {
      amount = 0
    }
  }

  const unit = toilRequest.isPartDay ? formatMessage({ id: amount === 1 ? 'app.hour' : 'app.hours' }) : formatMessage({ id: amount === 1 ? 'app.day' : 'app.days' })

  return `${amount} ${unit}`
}
